import { useState } from 'react'
import { X, CreditCard, Ban, RotateCcw, Activity, AlertCircle } from 'lucide-react'
import { PlanBadge, StatusChip, naira, orgMrr } from '../ui'
import { timeAgo } from '../../utils/timeAgo'
import { daysSince } from '../../hooks/useBranchUsage'

const ROLE_CLS = {
  admin: 'bg-blue-100 text-blue-700',
  manager: 'bg-green-100 text-green-700',
  staff: 'bg-gray-100 text-gray-600',
  superadmin: 'bg-purple-100 text-purple-700',
}

function fmtDateTime(d) {
  return d
    ? d.toLocaleString('en-NG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'
}

function Item({ label, value }) {
  return (
    <div>
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="text-sm text-gray-800 mt-0.5">{value ?? '—'}</p>
    </div>
  )
}

// Everything the signup form captured about an org + its members, so the
// superadmin never has to open Supabase to answer "who is this?".
export default function OrgDetail({ org, branches, users, onManage, onClose, usage, onSuspend, onRestore }) {
  const [confirmSuspend, setConfirmSuspend] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function handleSuspend() {
    setBusy(true); setError('')
    const result = await onSuspend(org.id, reason)
    setBusy(false)
    if (result?.success === false) return setError(result.message || 'Failed to suspend.')
    setConfirmSuspend(false); setReason('')
  }

  async function handleRestore() {
    setBusy(true); setError('')
    const result = await onRestore(org.id)
    setBusy(false)
    if (result?.success === false) setError(result.message || 'Failed to restore.')
  }

  const orgBranches = branches.filter((b) => b.organizationId === org.id)
  const members = users
    .filter((u) => u.organizationId === org.id)
    .sort((a, b) => (a.role === 'admin' ? -1 : 1) - (b.role === 'admin' ? -1 : 1))
  const mrr = orgMrr(org)

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-800 truncate">{org.name}</h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <PlanBadge plan={org.plan} />
              <StatusChip org={org} />
              {mrr > 0 && <span className="text-xs text-gray-500">{naira(mrr)}/mo</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition flex-none">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Signup details */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">Signup details</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 bg-gray-50 rounded-xl p-4">
              <Item label="Signed up" value={fmtDateTime(org.createdAt)} />
              <Item label="Business type" value={org.businessType} />
              <Item label="Company size" value={org.companySize} />
              <Item label="CAC number" value={org.cacNumber} />
              <Item label="Stated branches" value={org.numBranches} />
              <Item label="Location" value={org.location} />
              <Item label="Trial ends" value={org.trialEndsAt ? fmtDateTime(org.trialEndsAt) : 'No trial'} />
              <Item label="Actual branches" value={orgBranches.length > 0 ? orgBranches.map((b) => b.name).join(', ') : 'None'} />
            </div>
            {!org.businessType && !org.companySize && (
              <p className="text-xs text-gray-400 mt-2">
                No signup-form answers — this organization was likely created manually, not via self-service signup.
              </p>
            )}
          </div>

          {/* Per-branch usage */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5 flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5" /> Branch usage
            </h3>
            {!usage.available ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-xl px-4 py-5 text-center">
                Requires org_access_migration.sql.
              </p>
            ) : orgBranches.length === 0 ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-xl px-4 py-5 text-center">No branches.</p>
            ) : (
              <div className="border border-gray-100 rounded-xl overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="text-left px-3 py-2">Branch</th>
                      <th className="text-right px-3 py-2">Entries 30d</th>
                      <th className="text-right px-3 py-2">Credit 30d</th>
                      <th className="text-right px-3 py-2">Expenses 30d</th>
                      <th className="text-right px-3 py-2">Rows</th>
                      <th className="text-right px-3 py-2">Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgBranches.map((b) => {
                      const u = usage.byBranch.get(b.id)
                      const idle = daysSince(u?.lastActivityAt)
                      const dormant = idle === null || idle > 30
                      return (
                        <tr key={b.id} className="border-b border-gray-50 last:border-0">
                          <td className="px-3 py-2">
                            <p className="text-[13px] font-semibold text-gray-800">{b.name}</p>
                            <p className="text-[11px] text-gray-400">
                              {u ? `${u.users} users · ${u.tanks} tanks · ${u.pumps} pumps` : '—'}
                            </p>
                          </td>
                          <td className="px-3 py-2 text-right text-[13px] text-gray-700 tabular-nums">{u?.entries30d ?? 0}</td>
                          <td className="px-3 py-2 text-right text-[13px] text-gray-700 tabular-nums">{u?.creditSales30d ?? 0}</td>
                          <td className="px-3 py-2 text-right text-[13px] text-gray-700 tabular-nums">{u?.expenses30d ?? 0}</td>
                          <td className="px-3 py-2 text-right text-[13px] text-gray-500 tabular-nums">{u?.rowsTotal ?? 0}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <span className={`text-[12px] font-semibold ${dormant ? 'text-amber-700' : 'text-gray-600'}`}>
                              {u?.lastActivityAt ? timeAgo(u.lastActivityAt) : 'never'}
                            </span>
                            {dormant && (
                              <span className="ml-1.5 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                                dormant
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-2">
              "Rows" counts every branch-scoped record; dormant = no entry, sale, expense or capture in 30 days.
            </p>
          </div>

          {/* Access */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">Access</h3>
            {org.suspendedAt ? (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 space-y-2">
                <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
                  <Ban className="w-4 h-4" /> Suspended {timeAgo(org.suspendedAt)}
                </p>
                {org.suspendedReason && <p className="text-xs text-red-700">Reason: {org.suspendedReason}</p>}
                <p className="text-xs text-red-600">
                  Everyone in this organisation is locked out of the apps regardless of payment state.
                </p>
                <button onClick={handleRestore} disabled={busy}
                  className="flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-green-300 disabled:opacity-50 transition">
                  <RotateCcw className="w-3.5 h-3.5" /> {busy ? 'Restoring…' : 'Restore access'}
                </button>
              </div>
            ) : confirmSuspend ? (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 space-y-2">
                <p className="text-sm font-semibold text-red-800">Suspend {org.name}?</p>
                <p className="text-xs text-red-600">
                  Every member is locked out immediately on their next page load, even if they are paying.
                  Billing is untouched — this is separate from their subscription.
                </p>
                <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (shown to the customer)"
                  className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-red-400 bg-white" />
                {error && <p className="text-xs font-medium text-red-700">{error}</p>}
                <div className="flex gap-2 pt-0.5">
                  <button onClick={() => { setConfirmSuspend(false); setError('') }} disabled={busy}
                    className="flex-1 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-semibold disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleSuspend} disabled={busy || !reason.trim()}
                    className="flex-1 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-40">
                    {busy ? 'Suspending…' : 'Suspend'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-gray-700">Active</p>
                  <p className="text-[11px] text-gray-400">
                    Access follows their plan and payment state.
                    {org.graceUntil && org.graceUntil > new Date()
                      ? ` In payment grace until ${fmtDateTime(org.graceUntil)}.` : ''}
                  </p>
                </div>
                <button onClick={() => setConfirmSuspend(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:border-red-300 transition">
                  <Ban className="w-3.5 h-3.5" /> Suspend
                </button>
              </div>
            )}
          </div>

          {/* Members */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2.5">
              Members ({members.length})
            </h3>
            {members.length === 0 ? (
              <p className="text-sm text-gray-400 bg-gray-50 rounded-xl px-4 py-5 text-center">No members.</p>
            ) : (
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                {members.map((u) => (
                  <div key={u.id} className="px-4 py-3 border-b border-gray-100 last:border-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-gray-800 truncate">{u.fullName || u.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold capitalize ${ROLE_CLS[u.role] || ROLE_CLS.staff}`}>
                        {u.role}
                      </span>
                      {u.isActive === false && (
                        <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-red-100 text-red-700">Deactivated</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-gray-500">
                      <span className="font-mono">{u.email}</span>
                      {u.phone && <span>{u.phone}</span>}
                      <span>joined {u.joinedAt ? fmtDateTime(u.joinedAt) : '—'}</span>
                      <span>last seen {u.lastSeenAt ? timeAgo(u.lastSeenAt) : '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-2">
              "Last seen" starts tracking from each user's next sign-in (requires last_seen_migration.sql).
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
            Close
          </button>
          <button onClick={() => { onClose(); onManage(org) }}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 transition">
            <CreditCard className="w-4 h-4" /> Manage plan &amp; trial
          </button>
        </div>
      </div>
    </div>
  )
}
